import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import showToast from '../utils/toastHelper';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [status, setStatus] = useState(null);
  const [successfulPaymentType, setSuccessfulPaymentType] = useState(null);
  const [failureReturnPath, setFailureReturnPath] = useState('/dashboard');
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent duplicate verification calls
    if (hasVerified.current) return;
    hasVerified.current = true;
    
    verifyPayment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPaymentReturnPath = () => {
    const paymentType = searchParams.get('type');
    const subscriptionPayment = localStorage.getItem('pendingSubscriptionPayment') || paymentType === 'subscription';
    const electionPayment = localStorage.getItem('pendingElectionPayment');
    const guestVoteElectionId = localStorage.getItem('pendingGuestVoteElectionId');
    const voteElectionId = localStorage.getItem('pendingVoteElectionId');

    if (subscriptionPayment) return '/admin/billing';
    if (electionPayment === 'true') return '/admin/elections';
    if (guestVoteElectionId) return `/elections/${guestVoteElectionId}`;
    if (voteElectionId) return `/voter/elections/${voteElectionId}/vote`;
    return '/dashboard';
  };

  const clearPendingPaymentContext = () => {
    localStorage.removeItem('pendingSubscriptionPayment');
    localStorage.removeItem('pendingElectionPayment');
    localStorage.removeItem('pendingElectionId');
    localStorage.removeItem('pendingVoteElectionId');
    localStorage.removeItem('pendingGuestVoteElectionId');
  };

  const verifyPayment = async () => {
    const paymentReturnPath = getPaymentReturnPath();
    setFailureReturnPath(paymentReturnPath);
    try {
      const txRef = searchParams.get('tx_ref');
      // const transactionId = searchParams.get('transaction_id');
      const paymentType = searchParams.get('type');
      const voteElectionId = localStorage.getItem('pendingVoteElectionId');
      const guestVoteElectionId = localStorage.getItem('pendingGuestVoteElectionId');
      const electionPayment = localStorage.getItem('pendingElectionPayment');
      const electionId = localStorage.getItem('pendingElectionId');
      const subscriptionPayment = localStorage.getItem('pendingSubscriptionPayment') || paymentType === 'subscription';
      
      // Store these at component level for use in Go Back button
      window.voteElectionId = voteElectionId;
      window.guestVoteElectionId = guestVoteElectionId;

      if (!txRef) {
        setStatus('error');
        showToast('Invalid payment reference', 'error');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Use different endpoint based on payment type
      let verifyEndpoint;
      if (subscriptionPayment) {
        verifyEndpoint = `/subscriptions/verify?txRef=${txRef}`;
      } else if (electionPayment === 'true') {
        verifyEndpoint = `/payments/package/verify?txRef=${txRef}`;
      } else {
        verifyEndpoint = `/payments/verify?txRef=${txRef}`;
      }
      
      // Guest votes don't need auth token
      const headers = {};
      if (token && !guestVoteElectionId) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${verifyEndpoint}`,
        { headers }
      );

      if (response.data.success) {
        setStatus('success');
        
        // Check payment type and redirect accordingly
        if (subscriptionPayment) {
          setSuccessfulPaymentType('subscription');
          showToast('Subscription activated successfully!', 'success');
          localStorage.removeItem('pendingSubscriptionPayment');
          setTimeout(() => {
            navigate('/admin/billing', { replace: true });
          }, 2000);
        } else if (electionPayment === 'true' && electionId) {
          setSuccessfulPaymentType('package');
          showToast('Payment successful! Your election is now active.', 'success');
          
          // Clear pending flags
          localStorage.removeItem('pendingElectionPayment');
          localStorage.removeItem('pendingElectionId');
          
          // Redirect to elections page after 2 seconds
          setTimeout(() => {
            navigate('/admin/elections', { replace: true });
          }, 2000);
        } else if (voteElectionId) {
          setSuccessfulPaymentType('vote');
          showToast('Payment successful! Your vote has been cast.', 'success');
          
          // Clear pending election
          localStorage.removeItem('pendingVoteElectionId');
          
          // Redirect to voting page after 2 seconds
          setTimeout(() => {
            navigate(`/voter/elections/${voteElectionId}/vote`, { replace: true });
          }, 2000);
        } else if (guestVoteElectionId) {
          setSuccessfulPaymentType('vote');
          showToast('Payment successful! Your vote has been cast.', 'success');
          
          // Clear pending guest election
          localStorage.removeItem('pendingGuestVoteElectionId');
          
          // Redirect to public election page after 2 seconds
          setTimeout(() => {
            navigate(`/elections/${guestVoteElectionId}`, { replace: true });
          }, 2000);
        } else {
          // Fallback redirect
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 2000);
        }
      } else {
        setStatus('failed');
        showToast('Payment verification failed', 'error');
        
        // For failed election payments, redirect to elections page after delay
        if (electionPayment === 'true') {
          setTimeout(() => {
            clearPendingPaymentContext();
            navigate(paymentReturnPath, { replace: true });
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      // Only show error toast if it's not a duplicate transaction error
      const errorMsg = error.response?.data?.error || 'Payment verification failed';
      if (!errorMsg.includes('already been used')) {
        showToast(errorMsg, 'error');
      }
      
      // Clean up for failed election payments
      const electionPayment = localStorage.getItem('pendingElectionPayment');
      if (electionPayment === 'true') {
        setTimeout(() => {
          clearPendingPaymentContext();
          navigate(paymentReturnPath, { replace: true });
        }, 3000);
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="container-fluid d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', background: '#f7fafc' }}>
      <div className="card" style={{ maxWidth: '500px', width: '100%', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div className="card-body text-center p-5">
          {verifying ? (
            <>
              <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Verifying...</span>
              </div>
              <h4 className="mb-2">Verifying Payment</h4>
              <p className="text-muted">Please wait while we confirm your payment...</p>
            </>
          ) : status === 'success' ? (
            <>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <i className="bi bi-check-lg text-white" style={{ fontSize: '3rem' }}></i>
              </div>
              <h4 className="mb-2" style={{ color: '#10b981', fontWeight: '700' }}>Payment Successful!</h4>
              <p className="text-muted mb-4">
                {localStorage.getItem('pendingElectionPayment') === 'true' 
                  ? 'Your election package has been activated.'
                  : successfulPaymentType === 'subscription'
                    ? 'Your annual subscription has been activated.'
                    : 'Your vote has been cast successfully.'}
              </p>
              <p className="text-muted" style={{ fontSize: '0.875rem' }}>Redirecting you back...</p>
            </>
          ) : (
            <>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <i className="bi bi-x-lg text-white" style={{ fontSize: '3rem' }}></i>
              </div>
              <h4 className="mb-2" style={{ color: '#ef4444', fontWeight: '700' }}>Payment Failed</h4>
              <p className="text-muted mb-4">There was an issue processing your payment.</p>
              <button
                onClick={() => {
                  clearPendingPaymentContext();
                  navigate(failureReturnPath, { replace: true });
                }}
                className="btn"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.625rem 2rem',
                  fontWeight: '500',
                }}
              >
                Go Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
